import type { ScenarioRunSetModel } from '@/shared/generated'

import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { fromPairs, mapValues, pick } from 'lodash/fp'
import { BoxIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useMainGlobalPcSetQuery } from '@/entities/pricing-campaign'
import { useRunScenarioSetsMutation } from '@/entities/scenario'

import { client } from '@/shared/api/client'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider, RHFMultiSelect } from '@/shared/components/HookForm'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import { ampli } from '@/shared/lib/ampli'
import { queryClient } from '@/shared/lib/query-client'

import { prepareScenarioRuns } from '../lib/prepareScenarioRuns'
import { prepareValues } from '../lib/prepareValues'
import { useModalOptions } from '../lib/useModalOptions'
import { schema } from '../model/schema'
import { GlobalSetItem } from './GlobalSetItem'
import { RemoveCombinationButton } from './RemoveCombinationButton'

export function GlobalSetsCompareModal({
	isOpen,
	close,
	preselectedGlobalSetIds,
}: {
	isOpen: boolean
	close: () => void
	preselectedGlobalSetIds?: string[]
}) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(schema),
	})
	const globalSetIds = methods.watch('globalSetIds')
	const selectedValues = methods.watch('selectedValues')
	const optimizationGroupIds = methods.watch('optimizationGroupIds')
	const { optimizationGroupsOptions, globalSetsOptions, isLoading } =
		useModalOptions()
	const runScenarioSetsMutation = useRunScenarioSetsMutation({
		onSuccess: ({ id }: ScenarioRunSetModel) => {
			navigate({ to: `/p/whatif/runs/${id}/summary` })
			queryClient.invalidateQueries({
				queryKey: ['scenarios-run-sets'],
			})
			close()
		},
	})
	const { data: mainGlobalSet, isLoading: isMainGlobalSetLoading } =
		useMainGlobalPcSetQuery()
	useEffect(() => {
		if (preselectedGlobalSetIds?.length) {
			methods.setValue('globalSetIds', preselectedGlobalSetIds)
		}
	}, [preselectedGlobalSetIds])
	useEffect(() => {
		optimizationGroupIds &&
			methods.setValue(
				'selectedValues',
				mapValues(pick(optimizationGroupIds))(selectedValues) as Record<
					string,
					Record<string, string>
				>
			)
	}, [optimizationGroupIds])
	useEffect(() => {
		const values = fromPairs(
			globalSetIds?.map((id) => [
				id,
				selectedValues?.[id] ?? prepareValues(optimizationGroupIds),
			])
		)
		methods.setValue('selectedValues', values, { shouldDirty: true })
	}, [globalSetIds])
	const _handleSubmit = async ({
		optimizationGroupIds,
		selectedValues,
	}: any) => {
		ampli.whatIfSummaryRun()
		const toastId = toast.loading(
			t('general_processing', { defaultValue: 'Processing...' })
		)
		const queryParams = { optimization_group_ids: optimizationGroupIds }
		const scenarios = await queryClient.fetchQuery({
			queryKey: ['scenarios-query-list', queryParams],
			queryFn: () => client.scenario.scenariosQueryList(queryParams),
		})
		toast.dismiss(toastId)

		if (!scenarios?.length) return

		const requestBody = {
			scenario_runs: prepareScenarioRuns(scenarios, selectedValues!),
		}

		toast.promise(runScenarioSetsMutation.mutateAsync(requestBody), {
			loading: t('general_processing', { defaultValue: 'Processing...' }),
			error: t('whatif.compare.global_sets.run.error', {
				defaultValue: 'Error while running global sets',
			}),
		})
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal open={isOpen} onOpenChange={(open) => !open && close()}>
			<ModalContent className='max-w-4xl'>
				<FormProvider
					className='relative flex h-[90vh] w-full flex-col gap-4'
					methods={methods}
					onSubmit={handleSubmit}
				>
					<ModalHeader>
						<ModalTitle>
							{t('whatif.compare.global_sets.title', {
								defaultValue: 'Compare global sets',
							})}
						</ModalTitle>
						<ModalDescription>
							{t('whatif.compare.global_sets.description', {
								defaultValue:
									'Choose optimization groups and global sets, then pick a scenario per group for each set to run a comparison.',
							})}
						</ModalDescription>
					</ModalHeader>
					<ErrorBoundary>
						{/* Select optimization groups */}
						<RHFMultiSelect
							isLoading={isLoading}
							name='optimizationGroupIds'
							label={t('whatif.select_optimization_groups.label', {
								defaultValue: 'Select Optimization groups to compare',
							})}
							options={optimizationGroupsOptions || []}
						/>
						{/* Select global sets*/}
						<RHFMultiSelect
							className='w-full'
							isLoading={isLoading || isMainGlobalSetLoading}
							name='globalSetIds'
							label={t('whatif.select_global_sets.label', {
								defaultValue: 'Select global sets to compare',
							})}
							options={
								globalSetsOptions || [
									{
										value: mainGlobalSet?.id ?? '',
										label: mainGlobalSet?.name ?? '',
									},
								]
							}
						/>
						<ModalBody className='overflow-x-hidden border-y bg-card py-4'>
							<div className='ml-4 box-border flex w-full flex-col gap-2 border-l px-4'>
								{globalSetIds?.map((id, index) => (
									<div key={id} className='flex w-full flex-col gap-2 pb-10'>
										<div className='flex items-center gap-2'>
											<div className='-ml-8 flex size-7 shrink-0 items-center justify-center rounded-full border bg-muted'>
												<BoxIcon className='size-4 text-muted-foreground' />
											</div>
											<div className='flex w-full items-center gap-2 overflow-hidden'>
												<Badge>
													{t('combination', { defaultValue: 'Combination' })} #
													{index + 1}
												</Badge>
												<Separator className='shrink' />
												<RemoveCombinationButton id={id} />
											</div>
										</div>
										<GlobalSetItem id={id} key={id} />
									</div>
								))}
							</div>
						</ModalBody>

						<ModalFooter className='sm:justify-between'>
							<ModalClose asChild>
								<Button onClick={close}>{t('general_cancel')}</Button>
							</ModalClose>
							<Button
								variant='primary'
								type='submit'
								disabled={
									!globalSetIds?.length || !optimizationGroupIds?.length
								}
								onClick={handleSubmit}
							>
								{t('scenario.run.confirm.ok')}
							</Button>
						</ModalFooter>
					</ErrorBoundary>
				</FormProvider>
			</ModalContent>
		</Modal>
	)
}
